import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Lti1P3Provider,
  LtiLaunchContainer,
  LtiVersion,
} from '@ublend-npm/aula-schema';
import { LTI_1P3_START } from '@core/constants/endpoints';
import { currentUser } from '@core/selectors/user';
import { getCurrentSpaceId } from '@core/selectors/space';
import { getLtiProviders } from '@core/selectors/lti';

import { Lti1p3Form } from './Lti1p3Form';
import { lti1p3Start } from './lti1p3Start';
import { LtiRequestType } from './types';
import { Lti1p3EmbedModal } from '../../Assignments/Lti1p3Embed/Lti1p3EmbedModal';

export type Lti1P3LaunchProps = {
  providerId: string;
  resourceLinkId: string;
  resourceLinkTitle?: string;
  assignmentId?: string;
  assignmentFormData?: Record<string, unknown>;
  /** Force deep linking (create/selection). Ignored when assignmentId is set. */
  forceDeepLinking?: boolean;
  onSuccessLaunch: () => void;
  onErrorLaunch: (err: Error) => void;
};

const resolveRequestType = ({
  assignmentId,
  forceDeepLinking,
  isDeepLinking,
}: {
  assignmentId?: string;
  forceDeepLinking?: boolean;
  isDeepLinking?: boolean;
}): LtiRequestType => {
  if (assignmentId) {
    return LtiRequestType.RESOURCE_LINK;
  }
  if (forceDeepLinking || isDeepLinking) {
    return LtiRequestType.DEEP_LINKING;
  }
  return LtiRequestType.RESOURCE_LINK;
};

export const Lti1p3Launch = ({
  providerId,
  resourceLinkId,
  resourceLinkTitle,
  assignmentId,
  assignmentFormData,
  forceDeepLinking,
  onSuccessLaunch,
  onErrorLaunch,
}: Lti1P3LaunchProps) => {
  const user = useSelector(currentUser);
  const spaceId = useSelector(getCurrentSpaceId);
  const ltiProviders = useSelector(getLtiProviders);

  const [ltiSessionToken, setLtiSessionToken] = useState<string | undefined>();
  const [shouldLaunch, setShouldLaunch] = useState(true);
  const [embedOpen, setEmbedOpen] = useState(false);

  const formRef = useRef<HTMLFormElement | null>(null);
  const onSuccessLaunchRef = useRef(onSuccessLaunch);
  const onErrorLaunchRef = useRef(onErrorLaunch);

  useEffect(() => {
    onSuccessLaunchRef.current = onSuccessLaunch;
    onErrorLaunchRef.current = onErrorLaunch;
  }, [onSuccessLaunch, onErrorLaunch]);

  const { objectId: userId } = user || {};
  const provider = ltiProviders.find(
    (p) =>
      p.id === providerId &&
      p.ltiVersion === LtiVersion.ONE_POINT_THREE &&
      !p.disabled
  ) as
    | (Lti1P3Provider & { launchContainer?: LtiLaunchContainer })
    | undefined;
  const {
    loginUrl,
    targetUrl,
    iss,
    clientId,
    isDeepLinking,
    launchContainer,
    description,
  } = provider || {};
  const type = resolveRequestType({
    assignmentId,
    forceDeepLinking,
    isDeepLinking,
  });

  const useEmbedModal =
    type === LtiRequestType.RESOURCE_LINK &&
    launchContainer === LtiLaunchContainer.IFRAME;
  const formTarget = '_blank';

  useEffect(() => {
    if (shouldLaunch && !ltiSessionToken && userId && clientId) {
      lti1p3Start({
        url: LTI_1P3_START(),
        spaceId,
        clientId,
        type,
        userId,
        resourceLinkId,
        ...(resourceLinkTitle ? { resourceLinkTitle } : {}),
        ...(assignmentId ? { assignmentId } : {}),
        ...(assignmentFormData ? { assignmentFormData } : {}),
      })
        .then((ltiToken) => {
          setLtiSessionToken(ltiToken);
        })
        .catch((err) => {
          onErrorLaunchRef.current(err);
        });
    }
  }, [
    shouldLaunch,
    ltiSessionToken,
    userId,
    spaceId,
    clientId,
    type,
    resourceLinkId,
    resourceLinkTitle,
    assignmentId,
    assignmentFormData,
  ]);

  useEffect(() => {
    if (!shouldLaunch || !ltiSessionToken || !clientId) {
      return;
    }

    if (useEmbedModal) {
      setEmbedOpen(true);
      setShouldLaunch(false);
      // Do not call onSuccessLaunch yet — parent clears launch state and
      // would unmount this embed modal.
      return;
    }

    if (formRef.current) {
      formRef.current.requestSubmit();
      setLtiSessionToken(undefined);
      setShouldLaunch(false);
      onSuccessLaunchRef.current();
    }
  }, [shouldLaunch, ltiSessionToken, clientId, useEmbedModal]);

  const closeEmbed = () => {
    setEmbedOpen(false);
    setLtiSessionToken(undefined);
    onSuccessLaunchRef.current();
  };

  if (
    useEmbedModal &&
    embedOpen &&
    ltiSessionToken &&
    clientId &&
    loginUrl &&
    targetUrl &&
    iss
  ) {
    return (
      <Lti1p3EmbedModal
        title={resourceLinkTitle || description || 'LTI tool'}
        loginUrl={loginUrl}
        targetUrl={targetUrl}
        clientId={clientId}
        iss={iss}
        loginHint={ltiSessionToken}
        type={type}
        onClose={closeEmbed}
      />
    );
  }

  if (
    shouldLaunch &&
    !useEmbedModal &&
    ltiSessionToken &&
    clientId &&
    loginUrl &&
    targetUrl &&
    iss
  ) {
    return (
      <Lti1p3Form
        ref={formRef}
        loginUrl={loginUrl}
        targetUrl={targetUrl}
        clientId={clientId}
        iss={iss}
        type={type}
        loginHint={ltiSessionToken}
        target={formTarget}
      />
    );
  }

  return null;
};
